import type { IApiClient } from '../api'
import { ApplicationHttpError } from '../api/applicationClient'
import { useArenaStore } from '../store'

const EVENT_POLL_MS = 2_000
const BOOTSTRAP_RETRY_MAX_MS = 30_000
export const REMOTE_PROJECTION_STALE_MS = 30_000
const MAX_EVENT_PAGES_PER_TICK = 20

export interface RemoteStateControllerOptions {
  continuous?: boolean
}

/**
 * The only owner of Portal-derived remote state. It performs one bootstrap,
 * advances one authorized event cursor, and atomically refreshes the store.
 */
export class RemoteStateController {
  private stopped = true
  private timer: ReturnType<typeof setTimeout> | null = null
  private cursor: string | undefined
  private lastRefresh = 0
  private bootstrapFailures = 0
  private readonly continuous: boolean

  constructor(
    private readonly client: IApiClient,
    options: RemoteStateControllerOptions = {},
  ) {
    this.continuous = options.continuous ?? true
  }

  start(): void {
    if (!this.stopped) return
    this.stopped = false
    void this.bootstrap()
  }

  stop(): void {
    this.stopped = true
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
  }

  private async bootstrap(): Promise<void> {
    if (await this.refreshProjection()) {
      this.bootstrapFailures = 0
      if (this.continuous) this.schedule()
      return
    }
    if (!this.stopped && this.continuous) {
      // Retain an empty/last-good store, but keep recovery immediately due so
      // bootstrap retries independently. Do not start the comparatively costly
      // event long-poll until an authoritative base snapshot exists.
      this.lastRefresh = 0
      this.bootstrapFailures += 1
      const delay = Math.min(
        BOOTSTRAP_RETRY_MAX_MS,
        EVENT_POLL_MS * (2 ** Math.min(this.bootstrapFailures - 1, 4)),
      )
      this.timer = setTimeout(() => void this.bootstrap(), delay)
    }
  }

  private schedule(): void {
    if (this.stopped) return
    this.timer = setTimeout(() => void this.tick(), EVENT_POLL_MS)
  }

  private async tick(): Promise<void> {
    if (this.stopped) return
    let changed = false
    try {
      let pageCount = 0
      let hasMore = false
      do {
        const previousCursor = this.cursor
        const page = await this.client.pollRemoteEvents?.(this.cursor)
        if (page?.cursor) this.cursor = page.cursor
        changed ||= page?.changed === true
        hasMore = page?.hasMore === true
        pageCount += 1
        if (hasMore && (!page?.cursor || page.cursor === previousCursor)) {
          throw new Error('Remote event cursor did not advance')
        }
      } while (hasMore && pageCount < MAX_EVENT_PAGES_PER_TICK && !this.stopped)
      if (
        changed ||
        Date.now() - this.lastRefresh >= REMOTE_PROJECTION_STALE_MS
      ) {
        await this.refreshProjection()
      }
    } catch (error) {
      if (error instanceof ApplicationHttpError && error.status === 410) {
        // Portal no longer retains this cursor. Drop it and replace the full
        // authorized projection before reading events from the new head.
        this.cursor = undefined
        if (!(await this.refreshProjection())) this.lastRefresh = 0
      } else if (
        changed ||
        Date.now() - this.lastRefresh >= REMOTE_PROJECTION_STALE_MS
      ) {
        // Earlier pages may already have advanced the cursor. Replace the
        // projection before retaining that cursor, and keep fallback recovery
        // independent from the health of the event endpoint itself.
        await this.refreshProjection()
      }
      // Other transient failures retain cursor and last-good snapshot.
    } finally {
      this.schedule()
    }
  }

  private async refreshProjection(): Promise<boolean> {
    try {
      await useArenaStore.getState().hydrate()
      this.lastRefresh = Date.now()
      return true
    } catch {
      this.lastRefresh = 0
      useArenaStore.getState().markRemoteProjectionStale()
      return false
    }
  }
}
